const IDEA_MODES = new Set(['idea', 'given_idea', 'give_foundry_an_idea']);
const DECISION_PACK_SCHEMA_VERSION = 1;
const SOURCE_ARTIFACT_TYPES = new Set([
  'MARKET_RESEARCH',
  'COMMERCIAL_ANALYSIS',
  'DEVILS_ADVOCATE_REVIEW',
  'PRODUCT_STRATEGY',
  'SCORECARD',
]);

export const isIdeaRun = (run) => IDEA_MODES.has(String(run?.mode || '').trim().toLowerCase());

export const isEvidenceCluster = (opportunity) => {
  if (['CANONICAL_PROPOSITION', 'DISTINCT_OPPORTUNITY'].includes(opportunity?.candidate_kind)) return false;
  return opportunity?.candidate_kind === 'EVIDENCE_CLUSTER'
    || Boolean(opportunity?.consolidated_into_opportunity_id);
};

export const visibleFoundryOpportunities = (opportunities = []) => opportunities.filter((opportunity) => !isEvidenceCluster(opportunity));

const nonempty = (value) => typeof value === 'string' && value.trim().length > 0;
const canonicalContentHash = (value) => typeof value === 'string'
  && /^sha256:[0-9a-f]{64}$/.test(value);
const sameNullable = (left, right) => (left ?? null) === (right ?? null);
const hasExactKeys = (value, keys) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
};

const governedCandidateIds = (candidate) => {
  if (!nonempty(candidate?.id)) return null;
  const groupedIds = candidate?.grouped_opportunity_ids;
  if (groupedIds !== undefined && (!Array.isArray(groupedIds)
    || groupedIds.some((id) => !nonempty(id))
    || new Set(groupedIds).size !== groupedIds.length
    || groupedIds.includes(candidate.id))) return null;
  return [candidate.id, ...(groupedIds || [])];
};

const validSourceCandidates = (pack, candidate, run, opportunities) => {
  const refs = pack?.content?.source_candidates;
  const candidateIds = governedCandidateIds(candidate);
  if (!candidateIds || !Array.isArray(refs) || refs.length !== candidateIds.length) return false;
  const allowed = new Set(candidateIds);
  const actualById = new Map(opportunities.map((opportunity) => [opportunity?.id, opportunity]));
  return new Set(refs.map((ref) => ref?.id)).size === refs.length
    && refs.every((ref) => {
      const actual = actualById.get(ref?.id);
      return hasExactKeys(ref, ['id', 'discovery_run_id', 'content_hash'])
        && nonempty(ref.id)
        && allowed.has(ref.id)
        && canonicalContentHash(ref.content_hash)
        && Boolean(actual)
        && actual.candidate_identity_hash === ref.content_hash
        && sameNullable(ref.discovery_run_id, actual.discovery_run_id)
        && (run ? ref.discovery_run_id === run.id : !ref.discovery_run_id);
    });
};

const validSourceArtifacts = (pack, candidate, artifacts) => {
  const refs = pack?.content?.source_artifacts;
  const candidateIds = governedCandidateIds(candidate);
  if (!candidateIds) return false;
  const allowedCandidateIds = new Set(candidateIds);
  const actualById = new Map(artifacts.map((artifact) => [artifact?.id, artifact]));
  return Array.isArray(refs)
    && refs.length > 0
    && new Set(refs.map((ref) => ref?.id)).size === refs.length
    && refs.every((ref) => {
      const actual = actualById.get(ref?.id);
      return hasExactKeys(ref, ['id', 'candidate_id', 'artifact_type', 'revision', 'content_hash'])
      && nonempty(ref.id)
      && canonicalContentHash(ref.content_hash)
      && nonempty(ref.candidate_id)
      && allowedCandidateIds.has(ref.candidate_id)
      && SOURCE_ARTIFACT_TYPES.has(ref.artifact_type)
      && Number.isInteger(ref.revision)
      && ref.revision >= 1
      && Boolean(actual)
      && actual.opportunity_id === ref.candidate_id
      && actual.artifact_type === ref.artifact_type
      && actual.revision === ref.revision
      && actual.content_hash === ref.content_hash;
    });
};

const validSourceEvidence = (pack, candidate, run, evidence) => {
  const refs = pack?.content?.source_evidence;
  const candidateIds = governedCandidateIds(candidate);
  if (!candidateIds || !Array.isArray(refs) || refs.length === 0) return false;
  const allowed = new Set(candidateIds);
  const actualById = new Map(evidence.map((item) => [item?.id, item]));
  return new Set(refs.map((ref) => ref?.id)).size === refs.length
    && refs.every((ref) => {
      const actual = actualById.get(ref?.id);
      return hasExactKeys(ref, ['id', 'candidate_id', 'content_hash'])
        && nonempty(ref.id)
        && nonempty(ref.candidate_id)
        && allowed.has(ref.candidate_id)
        && canonicalContentHash(ref.content_hash)
        && Boolean(actual)
        && actual.opportunity_id === ref.candidate_id
        && actual.evidence_identity_hash === ref.content_hash
        && (run ? actual.discovery_run_id === run.id : !actual.discovery_run_id);
    });
};

const validPackKind = (pack, candidate, run, opportunities, artifacts, evidence) => {
  const content = pack?.content;
  if (!content || content.schema_version !== DECISION_PACK_SCHEMA_VERSION) return false;
  if (content.candidate_kind !== 'CANONICAL_PROPOSITION') return false;
  if (!validSourceCandidates(pack, candidate, run, opportunities)) return false;
  if (!validSourceArtifacts(pack, candidate, artifacts)) return false;
  if (!validSourceEvidence(pack, candidate, run, evidence)) return false;
  if (candidate?.decision_pack_kind !== content.synthesis_kind) return false;
  if (content.synthesis_kind === 'PROVIDER') {
    return nonempty(pack.provider)
      && nonempty(pack.model)
      && content.deterministic_fallback_used === false
      && !content.fallback_limitation;
  }
  return content.synthesis_kind === 'DETERMINISTIC_FALLBACK'
    && content.deterministic_fallback_used === true
    && typeof content.fallback_limitation === 'string'
     && content.fallback_limitation.trim().length > 0;
};

const runMatchesPack = (run, candidate, artifact) => {
  if (!run) {
    return !candidate?.discovery_run_id && !artifact?.content?.discovery_run_id;
  }
  const packIds = run.decision_pack_ids;
  if (
    !nonempty(run.id)
    || run.id !== candidate?.discovery_run_id
    || artifact?.content?.discovery_run_id !== run.id
    || !Array.isArray(packIds)
    || packIds.length === 0
    || packIds.some((id) => !nonempty(id))
    || new Set(packIds).size !== packIds.length
    || !packIds.includes(artifact.id)
  ) return false;
  if (isIdeaRun(run)) {
    return run.canonical_opportunity_id === candidate?.id
      && packIds.length === 1;
  }
  return !run.canonical_opportunity_id
    || run.canonical_opportunity_id === candidate?.id;
};

const packMatches = (artifact, candidate, run, opportunities, artifacts, evidence) => Boolean(
  artifact?.artifact_type === 'DECISION_PACK'
  && nonempty(candidate?.id)
  && nonempty(candidate?.canonical_opportunity_id)
  && nonempty(candidate?.decision_pack_id)
  && canonicalContentHash(candidate?.decision_pack_hash)
  && nonempty(artifact.id)
  && canonicalContentHash(artifact.content_hash)
  && Number.isInteger(artifact.revision)
  && artifact.revision >= 1
  && artifact.id === candidate?.decision_pack_id
  && artifact.content_hash === candidate?.decision_pack_hash
  && artifact.opportunity_id === candidate?.id
  && artifact.content?.canonical_opportunity_id === candidate?.id
  && candidate?.canonical_opportunity_id === candidate?.id
  && Number.isInteger(candidate?.state_version)
  && candidate.state_version >= 0
  && runMatchesPack(run, candidate, artifact)
  && validPackKind(artifact, candidate, run, opportunities, artifacts, evidence)
);

const decisionPackFor = (candidate, run, opportunities, artifacts, evidence) => {
  return artifacts.find((artifact) => packMatches(artifact, candidate, run, opportunities, artifacts, evidence)) || null;
};

const clusterBelongsTo = (cluster, candidate, run) => {
  const selectedRunId = run?.id || candidate?.discovery_run_id;
  const sameRun = selectedRunId
    ? cluster?.discovery_run_id === selectedRunId
    : !cluster?.discovery_run_id;
  return isEvidenceCluster(cluster)
  && sameRun
  && (
    cluster.consolidated_into_opportunity_id === candidate?.id
    || cluster.canonical_opportunity_id === candidate?.id
    || (candidate?.grouped_opportunity_ids || []).includes(cluster.id)
    || (
      Boolean(cluster.evidence_cluster_id)
      && (candidate?.evidence_cluster_ids || []).includes(cluster.evidence_cluster_id)
    )
  );
};

const synthesisReason = (run, candidate) => run?.synthesis_reason
  || run?.synthesis_blocked_reason
  || candidate?.synthesis_reason
  || candidate?.synthesis_blocked_reason
  || 'A valid immutable decision pack has not been produced for this proposition.';

const hasPackIdentity = (candidate, run, pack, opportunities, artifacts, evidence) => Boolean(
  packMatches(pack, candidate, run, opportunities, artifacts, evidence),
);

const reviewFor = (candidate, run, opportunities, artifacts, evidence) => {
  const pack = decisionPackFor(candidate, run, opportunities, artifacts, evidence);
  const synthesisState = candidate?.synthesis_state || run?.synthesis_state || 'BLOCKED';
  const reviewEligible = candidate?.review_eligible === true
    && candidate?.candidate_kind !== 'EVIDENCE_CLUSTER'
    && candidate?.state === 'AWAITING_REVIEW'
    && synthesisState === 'READY'
    && hasPackIdentity(candidate, run, pack, opportunities, artifacts, evidence);
  return {
    key: `${run?.id || 'standalone'}:${candidate?.id || 'missing-candidate'}`,
    run: run || null,
    candidate: candidate || null,
    decisionPack: pack,
    clusters: opportunities.filter((cluster) => clusterBelongsTo(cluster, candidate, run)),
    synthesisState: reviewEligible ? 'READY' : (synthesisState === 'READY' ? 'BLOCKED' : synthesisState),
    synthesisReason: reviewEligible ? '' : synthesisReason(run, candidate),
    reviewEligible,
  };
};

export function buildFoundryDecisionReviews({ opportunities = [], discoveryRuns = [], artifacts = [], evidence = [] } = {}) {
  const byId = new Map(opportunities.map((opportunity) => [opportunity.id, opportunity]));
  const reviews = [];
  const represented = new Set();

  discoveryRuns.filter(isIdeaRun).forEach((run) => {
    const canonicalId = run.canonical_opportunity_id;
    const candidate = byId.get(canonicalId)
      || opportunities.find((opportunity) => opportunity.discovery_run_id === run.id && opportunity.candidate_kind === 'CANONICAL_PROPOSITION')
      || opportunities.find((opportunity) => opportunity.discovery_run_id === run.id && !isEvidenceCluster(opportunity));
    if (candidate) represented.add(candidate.id);
    reviews.push(reviewFor(candidate, run, opportunities, artifacts, evidence));
  });

  visibleFoundryOpportunities(opportunities)
    .filter((candidate) => candidate.scout_disposition !== 'ARCHIVED' && !represented.has(candidate.id) && (
      candidate.state === 'AWAITING_REVIEW'
      || candidate.synthesis_state === 'BLOCKED'
      || candidate.synthesis_state === 'SYNTHESIS_BLOCKED'
    ))
    .forEach((candidate) => {
      const run = discoveryRuns.find((item) => item.id === candidate.discovery_run_id) || null;
      reviews.push(reviewFor(candidate, run, opportunities, artifacts, evidence));
    });

  return reviews;
}

export function runAccounting(run = {}) {
  const cost = Number(run.recorded_cost_usd ?? run.estimated_cost_usd ?? 0);
  const budget = Number(run.budget_usd ?? run.max_research_budget ?? 0);
  return {
    logicalRoleExecutions: Number(run.logical_role_executions ?? 0),
    providerDispatchAttempts: Number(run.provider_dispatch_attempts ?? run.model_calls ?? 0),
    providerSuccesses: Number(run.provider_successes ?? 0),
    providerFailures: Number(run.provider_failures ?? 0),
    providerUnknownOutcomes: Number(run.provider_unknown_outcomes ?? 0),
    billableCostUsd: cost,
    remainingBudgetUsd: Number(run.remaining_budget_usd ?? Math.max(0, budget - cost).toFixed(6)),
    budgetUsd: budget,
  };
}
